import React, { useState, useContext, useEffect } from "react";
import { useApolloClient, useQuery, useMutation } from "react-apollo";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Tag, Title, Tab, Field, Control, Button, Icon } from "rbx";
import {
  useParams,
  useHistory,
  useLocation,
  Route,
  Link,
  Switch
} from "react-router-dom";

import Loading from "../../../components/Loading";

import {
  prependMergeDownloadCSV,
  getUTCFormattedDate,
  ourToast as toast
} from "../../../utils";

import { ModalContext } from "../../../components/ModalProvider";

import {
  SINGLE_TARIFF_QUERY,
  UPDATE_TARIFF_MUTATION,
  DELETE_TARIFF_MUTATION,
  ALL_TARIFF_RATES_QUERY
} from "../../../graphql/Tariffs/Tariffs.graphql";

import Details from "./routes/Details";
import Rates from "./routes/Rates";
import { AddTariffRateForm } from "../../../components/Tariffs";
import { COLUMNS as TARIFF_COLUMNS, TARIFF_RATE_COLUMNS } from "./columns";
import { DeleteButton } from "../../../components/inputs";

const INITIAL_STATE = {
  TariffCode: "",
  Description: "",
  BeginDate: "",
  EndDate: "",
  MinLoad: "",
  MaxLoad: "",
  MinCharge: "",
  TariffTypeRecIdRef: "",
  FuelSurchargeRate: "",
  Active: true
};

const TariffPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();

  const client = useApolloClient();
  const { data } = useQuery(SINGLE_TARIFF_QUERY, {
    variables: { where: { RecId: parseInt(id) } }
  });
  const [updateTariff, { loading }] = useMutation(UPDATE_TARIFF_MUTATION, {
    refetchQueries: ["SINGLE_TARIFF_QUERY", "ALL_TARIFFS_QUERY"]
  });

  const [editing, setEditing] = useState(true);
  const [inputs, setInputs] = useState(INITIAL_STATE);
  const { setModalOpen } = useContext(ModalContext);

  useEffect(() => {
    if (data?.Tariff) {
      setInputs(data?.Tariff);
    }
  }, [data]);

  const toggleEditing = () => setEditing(!editing);

  const handleChange = e => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setInputs({ ...inputs, [e.target.name]: value });
  };

  const handleSave = async e => {
    e.preventDefault();
    try {
      const obj = Object.keys(INITIAL_STATE).reduce(
        (acc, curr) =>
          inputs[curr] !== null ? { ...acc, [curr]: inputs[curr] } : acc,
        {}
      );
      await updateTariff({
        variables: {
          data: {
            ...obj,
            BeginDate: inputs.BeginDate
              ? getUTCFormattedDate(inputs.BeginDate)
              : null,
            EndDate: inputs.EndDate
              ? getUTCFormattedDate(inputs.EndDate)
              : null,
            MinLoad: parseInt(obj.MinLoad),
            MaxLoad: parseInt(obj.MaxLoad),
            MinCharge: parseFloat(obj.MinCharge),
            TariffTypeRecIdRef: parseInt(obj.TariffTypeRecIdRef),
            FuelSurchargeRate: inputs.FuelSurchargeRate
              ? parseFloat(inputs.FuelSurchargeRate)
              : null
          },
          where: { RecId: parseInt(id) }
        }
      });
      setEditing(false);
      toast.success("Tariff updated successfully.");
    } catch (err) {
      toast.error("Error updating Tariff.");
    }
  };

  if (loading || !data?.Tariff) return <Loading />;
  return (
    <React.Fragment>
      <header className="page-head">
        <div className="page-head-start">
          <Title color="primary" size={1}>
            {inputs?.TariffCode}
          </Title>

          <div className="page-head-start--subheader">
            <Tag color={inputs?.Active ? "success" : "danger"}>
              {inputs?.Active ? "ACTIVE" : "INACTIVE"}
            </Tag>
            <Title subtitle>{inputs?.Description}</Title>
          </div>
        </div>
        <div className="page-head-end">
          {location.pathname.includes("details") ? (
            <Field kind="group">
              <Control>
                <Button
                  color="primary"
                  disabled={editing}
                  size="small"
                  onClick={toggleEditing}
                >
                  <Icon>
                    <FontAwesomeIcon icon="edit" />
                  </Icon>
                  <span>Edit</span>
                </Button>
              </Control>
              <Control>
                <DeleteButton
                  mutation={DELETE_TARIFF_MUTATION}
                  refetchQueries={["ALL_TARIFFS_QUERY", "SINGLE_TARIFF_QUERY"]}
                  size="small"
                  variables={{ where: { RecId: parseInt(id) } }}
                  onComplete={() => history.push("/dispatch/tariffs")}
                />
              </Control>
              <Control>
                <Button
                  color="success"
                  disabled={!editing}
                  size="small"
                  onClick={handleSave}
                >
                  <Icon>
                    <FontAwesomeIcon icon="save" />
                  </Icon>
                  <span>Save</span>
                </Button>
              </Control>
            </Field>
          ) : (
            <Field kind="group">
              <Control>
                <Button
                  color="primary"
                  size="small"
                  onClick={() =>
                    prependMergeDownloadCSV(
                      client,
                      {
                        query: SINGLE_TARIFF_QUERY,
                        where: { RecId: parseInt(id) },
                        dataName: "Tariff",
                        columns: TARIFF_COLUMNS
                      },
                      {
                        query: ALL_TARIFF_RATES_QUERY,
                        where: { TariffRecIdRef: parseInt(id) },
                        dataName: "TariffRates",
                        columns: TARIFF_RATE_COLUMNS
                      }
                    )
                  }
                >
                  <Icon>
                    <FontAwesomeIcon icon="file-export" />
                  </Icon>
                  <span>Export</span>
                </Button>
              </Control>
              <Control>
                <Button
                  color="success"
                  size="small"
                  onClick={() =>
                    setModalOpen(
                      true,
                      <AddTariffRateForm
                        id={id}
                        onComplete={() => setModalOpen(false, "")}
                      />
                    )
                  }
                >
                  <Icon>
                    <FontAwesomeIcon icon="plus" />
                  </Icon>
                  <span>Add</span>
                </Button>
              </Control>
            </Field>
          )}
        </div>
      </header>
      <Tab.Group kind="boxed">
        <Tab
          active={location.pathname.includes("details")}
          as={Link}
          tabIndex="-1"
          to="details"
        >
          <span>Details</span>
        </Tab>
        <Tab
          active={location.pathname.includes("rates")}
          as={Link}
          tabIndex="-1"
          to="rates"
        >
          <span>Rates</span>
        </Tab>
      </Tab.Group>
      <Switch>
        <Route path="/finance/tariffs/:id/details">
          <Details editing={editing} inputs={inputs} onChange={handleChange} />
        </Route>
        <Route path="/finance/tariffs/:id/rates">
          <Rates id={inputs?.RecId} />
        </Route>
      </Switch>
    </React.Fragment>
  );
};

TariffPage.propTypes = {};

export default TariffPage;
